/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import com.benasher44.uuid.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import systems.frictionless.impulse.rest.internal.UuidSerializer

// Note that the server does not return the password back to the client, because the server does not store the password
@Serializable
data class User(
    @Serializable(with = UuidSerializer::class) val id: Uuid,
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    val email: Email
) {
    init {
        require(firstName.isNotBlank()) { "First name cannot be blank" }
        require(
            firstName.length < MAX_FIRST_NAME_LENGTH
        ) { "First name must be less than $MAX_FIRST_NAME_LENGTH but is ${firstName.length}" }
        require(lastName.isNotBlank()) { "Last name cannot be blank" }
        require(lastName.length < MAX_LAST_NAME_LENGTH) { "Last name must be less than $MAX_LAST_NAME_LENGTH but is ${lastName.length}" }
    }

    /**
     * @return The standard printing of someone's full name, e.g. First Last
     */
    fun fullName() = "$firstName $lastName"

    /**
     * For creating or updating a user.
     *
     * After a user is created, client should get a session to authenticate.
     */
    @Serializable
    data class Builder(
        @SerialName("first_name") val firstName: String,
        @SerialName("last_name") val lastName: String,
        val email: Email,
        val password: Password
    ) {
        init {
            // These are important; they will run on the server and deal with validation there.
            // An improvement would be to somehow capture each of these cases to return each specific validation
            // failure back to the client.
            require(firstName.isNotBlank()) { "First name cannot be blank" }
            require(firstName.length <= MAX_FIRST_NAME_LENGTH) { "First name must be <= $MAX_FIRST_NAME_LENGTH but is ${firstName.length}" }
            require(lastName.isNotBlank()) { "Last name cannot be blank" }
            require(lastName.length <= MAX_LAST_NAME_LENGTH) { "Last name must be <= $MAX_LAST_NAME_LENGTH but is ${lastName.length}" }

            // Email will validate itself
        }

        companion object {
            /**
             * Provides a basic local check of first name.  The server may still reject the value.
             */
            fun preflightFirstName(firstName: String) = firstName.isNotBlank()

            /**
             * Provides a basic local check of last name.  The server may still reject the value.
             */
            fun preflightLastName(lastName: String) = lastName.isNotBlank()

            /**
             * Provides a basic local check of email.  The server may still reject the value.
             */
            fun preflightEmail(email: String) = EmailValidator.isValid(email)
        }
    }

    companion object {
        fun new(
            uuid: Uuid,
            userBuilder: User.Builder
        ) = User(uuid, userBuilder.firstName, userBuilder.lastName, userBuilder.email)

        const val MAX_FIRST_NAME_LENGTH = 50
        const val MAX_LAST_NAME_LENGTH = 50
        const val MAX_EMAIL_LENGTH = 256
    }
}
